import { DatePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Table } from 'exceljs';
import { DynamicColumnConfig, DynamicTableConfig, DynamicTableFilter, ExportEvent } from 'src/app/shared/dynamic-table/dynamic-table.model';
import { RestrictionModel } from 'src/models/restriction.model';
import { RestrictionService } from 'src/services/restriction.service';
import { TemplateRef } from '@angular/core';
import { AppConfig } from 'src/app/app.config';
import { DynamicTableComponent } from 'src/app/shared/dynamic-table/dynamic-table.component';
import { SHARED_IMPORTS } from 'src/app/shared/shared.module';

@Component({
  selector: 'app-restriction',
  templateUrl: './restriction.component.html',
  styleUrls: ['./restriction.component.css'],
  standalone: true,
  imports: [...SHARED_IMPORTS, DynamicTableComponent]
})
export class RestrictionComponent implements OnInit, AfterViewInit {

  datePipe = new DatePipe('en-US');
  restrictionDetails: RestrictionModel[] = [];
  tableConfig: DynamicTableConfig = {};
  dynamicColumns: DynamicColumnConfig[] = [];
  isSearchFilterApplied: boolean;
  isLoading: boolean = false;
  @ViewChild('dt') tableData: Table;
  @ViewChild('scrollTop', { static: true }) scrollTop: ElementRef;
  @ViewChild('nameTemplate', { static: true }) nameTemplate: TemplateRef<any>;
  @ViewChild('emailTemplate', { static: false }) emailTemplate: TemplateRef<any>;
  cellTemplates: { [key: string]: TemplateRef<any> } = {};

  constructor(
    private restrictionService: RestrictionService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.initializeTable();
    this.getRestrictsApplicantDetails();

  }

  ngAfterViewInit(): void {
    // Assign the template after view init
    this.cellTemplates = {
      nameTemplate: this.nameTemplate,
      emailTemplate: this.emailTemplate
    };
  }

  initializeTable() {
    // Initialize dynamic table configuration
    this.tableConfig = {
      rows: 50,
      selectable: false,
      selectionMode: 'multiple',
      globalSearch: true,
      exportable: false,
      deletable: false,
      lazy: false,
      dataKey: 'applicantRestrictionId',
      preferenceKey: 'RestrictionTable',
      supportFilterHierarchy: true,
      title: 'Restriction Records',
      columnFilteringEnabled: false,
      addable: true
    };

    // Initialize dynamic columns configuration
    this.dynamicColumns = [
      { field: 'Participant_Name', header: 'Participant Name', width: '120px', sortable: true, filterable: true, filterType: 'multiselect', filterField: 'Participant_Name', filterOptions: [], dataType: 'text', customTemplate: 'nameTemplate' },
      { field: 'DOB', header: 'DOB', width: '120px', sortable: true, filterable: true, filterType: 'multiselect', filterField: 'DOB', filterOptions: [], dataType: 'date' },
      { field: 'Phone', header: 'Phone', width: '120px', sortable: true, filterable: true, filterType: 'multiselect', filterField: 'Phone', filterOptions: [], dataType: 'text' },
      { field: 'EmailId', header: 'Email Address', width: '180px', sortable: true, filterable: true, filterType: 'multiselect', filterField: 'EmailId', filterOptions: [], dataType: 'text', customTemplate: 'emailTemplate' },
      { field: 'AppSuiteUnit', header: 'Apt/Suite/Unit', width: '120px', sortable: true, filterable: true, filterType: 'multiselect', filterField: 'AppSuiteUnit', filterOptions: [], dataType: 'text' },
      { field: 'City', header: 'City', width: '120px', sortable: true, filterable: true, filterType: 'multiselect', filterField: 'City', filterOptions: [], dataType: 'text' },
      { field: 'State', header: 'State', width: '90px', sortable: true, filterable: true, filterType: 'multiselect', filterField: 'State', filterOptions: [], dataType: 'text' },
      { field: 'ZipCode', header: 'Zip Code', width: '100px', sortable: true, filterable: true, filterType: 'multiselect', filterField: 'ZipCode', filterOptions: [], dataType: 'text' },
      { field: 'Country', header: 'Country', width: '120px', sortable: true, filterable: true, filterType: 'multiselect', filterField: 'Country', filterOptions: [], dataType: 'text' },
    ];
  }
  getRestrictsApplicantDetails() {
    this.isLoading = true;
    this.restrictionService.getRestrictsApplicantDetails().subscribe((data: RestrictionModel[]) => {
      Promise.resolve().then(() => {
        this.restrictionDetails = data;
        this.restrictionDetails.forEach((item) => {
          item.Participant_Name = `${item.FirstName} ${item.MiddleInitial ? item.MiddleInitial + ' ' : ''}${item.LastName}`;
          item.DOB = this.datePipe.transform(item.DOB, 'MM/dd/yyyy');
        });

        this.dynamicColumns.forEach(col => {
          const uniqueValues = Array.from(new Set(this.restrictionDetails.map(item => item[col.field]).filter(v => v !== undefined && v !== null)));
          col.filterOptions = uniqueValues.map(val => ({ label: val, value: val, checked: true }));
        });
        this.isLoading = false;
        this.cdr.markForCheck();
      });
    });
  }

  /**
 * Helper to disable selection based on permissions
 */
  disableRowSelection = (row: any): boolean => {
    return false;
  }

  onDynamicFilterChange(filterState: DynamicTableFilter): void {
    // Reset search filter flag if a new filter is applied
    this.isSearchFilterApplied = false;

    let filtered = [...this.restrictionDetails];

    // Apply global search if present
    if (filterState.searchTerm && filterState.searchTerm.trim() !== '') {
      const searchTerm = filterState.searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        this.dynamicColumns.some(col => {
          const val = item[col.field];
          return val !== null && val !== undefined && val.toString().toLowerCase().includes(searchTerm);
        })
      );
      this.isSearchFilterApplied = true;
    }
  }

  onDynamicExport(event: ExportEvent): void {
    //this.exportAuditByFilterAsXLSX();
  }

  onDynamicPageChange(event: any): void {
    this.scrollTop.nativeElement.focus();
  }

  onDynamicSelectionChange(event: any) { }

  onFilterOptionsRefresh(event: any): void { }

  onDynamicDelete(event: any): void { }

  addNewRestriction(): void {
    this.router.navigate(['/adminhome/AddRestriction', 0]);
  }

  onParticipantNameClick(row: RestrictionModel) {
    return `${AppConfig.appsettings.navigatorRoot}admin#/adminhome/AddRestriction/${row.RestrictApplicantId}`;
  }
}
